import { all, call, put, takeLatest } from 'redux-saga/effects';

import analytics from '@xto10x/core/lib/analytics';
import {
  showAppLoading,
  hideAppLoading,
  showAlertMessage
  // fetchUserInfo
} from 'containers/App/store/actions';
import { areSameObjects } from '@xto10x/core/lib/app.utils';
import {
  actions,
  saveUsersList,
  fetchUsersList,
  getFounderDetailsResponse,
  getDirectReportsInOrgHierarchyResponse,
  validateHierarchyRequest,
  validateHierarchyResponse,
  getL1LeadersResponse,
  getFunctionsWithOwnerResponse,
  createFunctionResponse,
  removeFunctionResponse,
  getFunctionsWithOwnerRequest,
  getL1LeadersRequest,
  getFounderDetailsRequest,
  saveDemographicMasterData,
  saveOrgDemographicKeys,
  getDemographicMasterData,
  saveOrgSubscriptionResponse,
  saveHrbpsResponse,
  updateHrbpListResponse,
  updateManagerHrbpsResponse,
  removeHrbpListResponse,
  saveOngoingUserInviteStatus,
  getHrbpsRequest
} from './actions';
import {
  AsyncBulkInviteConstants,
  ModuleNames
} from '../../../utils/constants';
import { createHrbpUpdateRequestBody } from '../../../utils/inviteUtils';
import { AnalyticsCategory } from '../../../utils/analytics.constants';
import userApi from '../../../services/userApi';
import orgApi from '../../../services/orgApi';

function* fetchUsersListWorker({ burstCache }) {
  try {
    yield put(showAppLoading());
    const { data } = yield userApi.getUserList({ burstCache });
    yield put(saveUsersList(data, true));
    yield put(hideAppLoading());
  } catch (e) {
    analytics.trackError(e);
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
  }
}

function* fetchUsersListV3Worker({ burstCache, includeDemographics }) {
  try {
    yield put(showAppLoading());
    const { data } = yield userApi.getUserListV3({
      burstCache,
      includeDemographics
    });
    const { data: moduleAccess } = yield orgApi.getOrgAccessibleModules();
    const allowedModules = [ModuleNames.ADVANCED_OKR];
    const hasAccess = allowedModules.some(module =>
      moduleAccess.includes(module)
    );
    // should call getAdvancedOKRUsers api only when we have okr module access
    if (hasAccess) {
      const { data: advancedOkrResponse } = yield userApi.getAdvancedOKRUsers(
        ModuleNames.ADVANCED_OKR
      );
      data.advancedOKRUsers = advancedOkrResponse.users;
    } else {
      data.advancedOKRUsers = [];
    }
    yield put(saveUsersList(data, false));
    yield put(hideAppLoading());
  } catch (e) {
    analytics.trackError(e);
    yield put(hideAppLoading());
  }
}

function* inviteUsersWorker({ users, callback, sendEmailToggle }) {
  try {
    yield put(showAppLoading());
    analytics.trackEvent(
      AnalyticsCategory.cta_click,
      'settings',
      'people',
      'invite'
    );
    const { data } = yield userApi.inviteUsers(sendEmailToggle, users);
    yield put(showAlertMessage('success', 'Invitation was sent successfully'));
    yield put(fetchUsersList({ burstCache: true }));
    yield put(getFounderDetailsRequest());
    yield put(getDemographicMasterData());
    if (callback) callback(data);
    yield put(hideAppLoading());
  } catch (e) {
    yield put(
      showAlertMessage(
        'error',
        'Invite/ Update Failed. Please contact support team'
      )
    );
    analytics.trackError(e);
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
  }
}

function* bulkInviteUsersAsyncWorker({ users, callback, sendEmailToggle }) {
  try {
    yield put(showAppLoading());
    analytics.trackEvent(
      AnalyticsCategory.cta_click,
      'settings',
      'people',
      'invite'
    );
    const {
      data: isOngoingUserInviteData
    } = yield userApi.getIsOngoingUserInvite();
    if (
      isOngoingUserInviteData?.bulk_user_invite_request_status === 'IN_PROGRESS'
    ) {
      yield put(
        showAlertMessage(
          'error',
          AsyncBulkInviteConstants.DISABLED_TOOLTIP_TEXT
        )
      );
      return;
    }
    const { data } = yield userApi.bulkInviteUsersAsync(sendEmailToggle, users);
    yield put(
      saveOngoingUserInviteStatus(data.bulk_user_invite_request_status)
    );
    yield put(fetchUsersList({ burstCache: true }));
    yield put(getFounderDetailsRequest());
    yield put(getDemographicMasterData());
    if (callback) callback(data);
    yield put(hideAppLoading());
  } catch (e) {
    analytics.trackError(e);
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
  }
}

function* getIsOngoingUserInviteWorker({ callback }) {
  try {
    const { data } = yield userApi.getIsOngoingUserInvite();
    yield put(
      saveOngoingUserInviteStatus(data.bulk_user_invite_request_status)
    );
    if (callback) callback();
  } catch (e) {
    analytics.trackError('get_is_ongoing_user_invite_worker', e);
    yield put(showAlertMessage('error', e.message));
  }
}

function* revokeInviteWorker({ requestBody }) {
  try {
    yield put(showAppLoading());
    yield userApi.updateUser(requestBody);
    yield put(showAlertMessage('success', 'Invitation revoked'));
    yield put(fetchUsersList({ burstCache: true }));
    yield put(getFounderDetailsRequest());
    analytics.trackEvent(
      AnalyticsCategory.cta_click,
      'settings',
      'people',
      'revoke_invite'
    );
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
  }
}

function* changeRoleWorker({ user_reference_id, role }) {
  try {
    yield put(showAppLoading());
    yield userApi.updateUser({
      user_reference_id,
      role
    });
    // yield put(fetchUserInfo());
    yield put(showAlertMessage('success', 'User role changed'));
    yield put(fetchUsersList({ burstCache: true }));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
  }
}

function* revokeUserAccessWorker({ requestBody, newOwner, userType }) {
  try {
    yield put(showAppLoading());
    // eslint-disable-next-line no-param-reassign
    delete requestBody.image; // TODO: backend will create new api for revoke. This then removing this field
    if (newOwner) yield userApi.updateOkrUser(requestBody, newOwner);
    else yield userApi.updateUser(requestBody);

    yield put(fetchUsersList({ burstCache: true }));
    yield put(getFounderDetailsRequest());
    yield put(
      showAlertMessage(
        'success',
        `${userType === 'ACTIVE' ? 'User removed' : 'Invitation revoked.'}`
      )
    );
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
  }
}

function* getDirectReportsInOrgHierarchy({
  user_reference_id,
  parent,
  viaSearch
}) {
  try {
    yield put(showAppLoading());
    const { data } = yield userApi.getDirectReportsInOrgHierarchy(
      user_reference_id
    );
    yield put(
      getDirectReportsInOrgHierarchyResponse(
        user_reference_id,
        parent,
        data,
        viaSearch
      )
    );
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
  }
}

function* getFounderDetails({ expandL1 }) {
  try {
    yield put(showAppLoading());
    const { data } = yield userApi.getFounderDetails();
    yield put(getFounderDetailsResponse(data));
    if (expandL1) {
      yield all(
        data.founders?.map(founder => {
          if (!founder.org_subtree_count || founder.designation !== 'FOUNDER')
            return false;
          const { user_reference_id } = founder;
          const parent = '_root';
          return call(getDirectReportsInOrgHierarchy, {
            user_reference_id,
            parent
          });
        })
      );
    }
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
  }
}

function* validateHierarchy() {
  try {
    yield put(showAppLoading());
    const { data } = yield userApi.validateHierarchy();
    yield put(validateHierarchyResponse(data));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
  }
}

function* updateManagers({ users }) {
  try {
    yield put(showAppLoading());
    const { data } = yield userApi.updateManager(users);
    yield put(
      showAlertMessage('success', `${data.success.length} error resolved`)
    );
    yield put(hideAppLoading());
    yield put(validateHierarchyRequest());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
  }
}

function* updateDesignations({ users }) {
  try {
    yield put(showAppLoading());
    const { data } = yield userApi.updateDesignation(users);
    yield put(
      showAlertMessage('success', `${data.success.length} error resolved`)
    );
    yield put(hideAppLoading());
    yield put(validateHierarchyRequest());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
  }
}

function* getL1Leaders() {
  try {
    yield put(showAppLoading());
    const { data } = yield userApi.getL1Leaders();
    yield put(getL1LeadersResponse(data));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    analytics.trackError('get_l1_leaders_worker', e, false, false);
  }
}

function* getFunctionsWithOwner() {
  try {
    yield put(showAppLoading());
    const { data } = yield userApi.getFunctionsWithOwner();
    yield put(getFunctionsWithOwnerResponse(data));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
  }
}

function* createFunction({ name, owner }) {
  try {
    yield put(showAppLoading());
    const { data } = yield userApi.createFunctionWithOwner({ name, owner });
    yield put(createFunctionResponse(data));
    const functionName = data.name.charAt(0).toUpperCase() + data.name.slice(1);
    yield put(
      showAlertMessage(
        'success',
        `New function '${functionName}' has been added`
      )
    );
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
  }
}

function* removeFunction({ functionId, skipRemovalUpdate = false, cb }) {
  try {
    yield put(showAppLoading());
    const { data } = yield userApi.removeFunctionOwner(functionId);
    if (!skipRemovalUpdate) yield put(removeFunctionResponse(data));
    if (cb) {
      cb();
    }
    if (!skipRemovalUpdate) {
      const functionName =
        data.name.charAt(0).toUpperCase() + data.name.slice(1);
      yield put(
        showAlertMessage('success', `Function '${functionName}' removed`)
      );
      yield put(hideAppLoading());
    }
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
  }
}

function* updateFunctionOwners({ functionOwnerMap, oldFunctionIds, cb }) {
  try {
    yield put(showAppLoading());
    if (oldFunctionIds?.length) {
      yield all(
        oldFunctionIds?.map(id =>
          call(removeFunction, { functionId: id, skipRemovalUpdate: true })
        )
      );
    }
    yield userApi.updateFunctionOwners(functionOwnerMap);
    yield put(getL1LeadersRequest());
    yield put(getFunctionsWithOwnerRequest());
    yield put(showAlertMessage('success', 'Functions updated for L1 leaders'));
    if (cb) cb();
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
  }
}

function* getDemographicMasterDataForOrg() {
  try {
    yield put(showAppLoading());
    const { data } = yield userApi.getOrgDemographicsMasterData();
    yield put(saveDemographicMasterData(data));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('fetch_demographic_data_failed', e.message));
  }
}

function* updateUserDetailsWorker({ requestBody }) {
  try {
    yield put(showAppLoading());
    yield userApi.updateUser(requestBody);
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('update_user_details_worker', e.message));
  }
}

function* updateUserDemographicsDetailsWorker({ requestBody, callback }) {
  try {
    yield put(showAppLoading());
    yield userApi.updateUserDemographicsData(requestBody);
    yield call(getDemographicMasterDataForOrg);
    yield call(fetchUsersListWorker, { burstCache: true });
    yield put(showAlertMessage('success', 'Details updated for the user'));
    if (callback) callback();
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(
      showAlertMessage('update_user_demographics_details_worker', e.message)
    );
  }
}

function* getOrgDemographicKeysWorker() {
  try {
    yield put(showAppLoading());
    const { data } = yield userApi.getOrgDemographicWhitelistedKeys();
    yield put(saveOrgDemographicKeys(data));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('get_org_demographics_key_worker', e.message));
  }
}

function* updateUserRoleWorker({ requestBody }) {
  try {
    yield put(showAppLoading());
    yield userApi.updateRoles(requestBody);
    yield put(fetchUsersList({ burstCache: true }));
    yield put(showAlertMessage('success', 'User permissions updated'));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('Error updating user role', e.message));
  }
}

function* getOrgSubscription() {
  try {
    const { data } = yield orgApi.getAllSubscriptionsForOrg();
    yield put(saveOrgSubscriptionResponse(data));
  } catch (e) {
    analytics.trackError('get_org_subscription_worker', e);
    yield put(showAlertMessage('error', e.message));
  }
}

function* getHrbpsListWorker() {
  try {
    yield put(showAppLoading());
    const { data } = yield userApi.getAllHrbps();
    yield put(saveHrbpsResponse(data));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    analytics.trackError('get_hrbps_worker', e);
    yield put(showAlertMessage('error', e.message));
  }
}

function* updateHrbpsListWorker({ requestBody, toastMessage }) {
  try {
    yield put(showAppLoading());
    yield userApi.updateHrbps(requestBody);
    yield put(updateHrbpListResponse(requestBody));
    if (toastMessage) {
      yield put(showAlertMessage('success', toastMessage));
    } else {
      yield put(showAlertMessage('success', 'HRBPs updated successfully'));
    }
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    analytics.trackError('update_hrbps_worker', e);
    yield put(showAlertMessage('error', e.message));
  }
}

function* removeHrbpsListWorker({ requestBody, toastMessage }) {
  try {
    yield put(showAppLoading());
    yield userApi.removeHrbps(requestBody);
    yield put(removeHrbpListResponse(requestBody));
    if (toastMessage) {
      yield put(showAlertMessage('success', toastMessage));
    } else {
      yield put(showAlertMessage('success', 'HRBPs updated successfully'));
    }
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    analytics.trackError('update_hrbps_worker', e);
    yield put(showAlertMessage('error', e.message));
  }
}

function* updateManagerHrbpsWorker({ managerId, requestBody, callback }) {
  try {
    yield put(showAppLoading());
    const { data } = yield userApi.updateUserHrbpMapping(
      managerId,
      requestBody
    );
    yield put(updateManagerHrbpsResponse(managerId, data));
    // from invite flow
    if (callback) {
      put(getHrbpsRequest());
      callback();
    } else yield put(showAlertMessage('success', 'HRBPs updated successfully'));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    analytics.trackError('update_hrbps_worker', e);
    yield put(showAlertMessage('error', e.message));
  }
}

function* updateUserManagerDesignationOrDemographics({
  userAttributes,
  originalUserObject,
  allUsers,
  callback
}) {
  try {
    yield put(showAppLoading());
    // 1. check if manager/designation needs to be updated
    if (originalUserObject.managerEmail !== userAttributes.managerEmail) {
      // check if the manager is set as founder
      if (userAttributes.managerEmail.trim().toLowerCase() === 'founder') {
        yield userApi.updateDesignation([
          {
            user_reference_id: originalUserObject.id,
            designation: 'FOUNDER'
          }
        ]);
      } else {
        // else update manager with the email given
        yield userApi.updateManager([
          {
            user_reference_id: originalUserObject.id,
            manager_reference_id: allUsers.filter(
              user => user.email === userAttributes.managerEmail
            )[0].user_reference_id
          }
        ]);
      }
    }

    // 2. check if demographics needs to be updated
    if (
      !areSameObjects(
        originalUserObject.user_attributes,
        userAttributes.user_attributes
      )
    ) {
      const requestBody = {
        user_reference_id: originalUserObject.id,
        user_attributes: {
          ...userAttributes.user_attributes
        }
      };
      yield userApi.updateUserDemographicsData(requestBody);
    }

    // 3. check if hrbp needs to be updated
    if (
      !areSameObjects(originalUserObject.hrbpEmails, userAttributes.hrbpEmails)
    ) {
      yield userApi.updateUserHrbpMapping(
        originalUserObject.id,
        createHrbpUpdateRequestBody(
          originalUserObject.hrbpEmails.map(hrbp => hrbp.id),
          userAttributes.hrbpEmails.map(hrbp => hrbp.id)
        )
      );
    }
    if (callback) callback();

    // call other sagas to update the existing data which will be impacted by the changes
    yield call(fetchUsersListWorker, { burstCache: true });
    yield call(getFounderDetails, { expandL1: false });
    yield call(getDemographicMasterDataForOrg);
    yield put(showAlertMessage('success', 'User updated successfully'));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    analytics.trackError(
      'update_user_manager_Designation_Demographic_or_hrbp_worker',
      e
    );
    yield put(showAlertMessage('error', e.message));
  }
}

export default function* defaultSaga() {
  yield takeLatest(actions.FETCH_USERS_LIST, fetchUsersListWorker);
  yield takeLatest(actions.FETCH_USERS_LIST_V3, fetchUsersListV3Worker);
  yield takeLatest(actions.INVITE_USERS, inviteUsersWorker);
  yield takeLatest(actions.REVOKE_INVITE, revokeInviteWorker);
  yield takeLatest(actions.CHANGE_ROLE, changeRoleWorker);
  yield takeLatest(actions.REVOKE_USER_ACCESS, revokeUserAccessWorker);
  yield takeLatest(actions.GET_FOUNDER_DETAILS_REQUEST, getFounderDetails);
  yield takeLatest(
    actions.GET_DIRECT_REPORTS_IN_ORG_HIERARCHY_REQUEST,
    getDirectReportsInOrgHierarchy
  );
  yield takeLatest(actions.VALIDATE_HIERARCHY_REQUEST, validateHierarchy);
  yield takeLatest(actions.UPDATE_MANAGERS_REQUEST, updateManagers);
  yield takeLatest(actions.UPDATE_DESIGNATION_REQUEST, updateDesignations);
  yield takeLatest(actions.GET_L1LEADERS_REQUEST, getL1Leaders);
  yield takeLatest(
    actions.GET_FUNCTIONS_WITH_OWNER_REQUEST,
    getFunctionsWithOwner
  );
  yield takeLatest(actions.CREATE_FUNCTION_REQUEST, createFunction);
  yield takeLatest(actions.REMOVE_FUNCTION_REQUEST, removeFunction);
  yield takeLatest(
    actions.UPDATE_FUNCTION_OWNERS_REQUEST,
    updateFunctionOwners
  );
  yield takeLatest(
    actions.GET_DEMOGRAPHIC_MASTER_DATA_REQUEST,
    getDemographicMasterDataForOrg
  );
  yield takeLatest(
    actions.UPDATE_USER_DETAILS_REQUEST,
    updateUserDetailsWorker
  );
  yield takeLatest(
    actions.UPDATE_USER_DEMOGRAPHICS_REQUEST,
    updateUserDemographicsDetailsWorker
  );
  yield takeLatest(
    actions.GET_ORG_DEMOGRAPHIC_KEYS,
    getOrgDemographicKeysWorker
  );
  yield takeLatest(actions.UPDATE_USER_ROLE, updateUserRoleWorker);
  yield takeLatest(actions.GET_ORG_SUBSCRIPTION_REQUEST, getOrgSubscription);
  yield takeLatest(actions.GET_HRBPS_LIST_REQUEST, getHrbpsListWorker);
  yield takeLatest(actions.UPDATE_HRBP_LIST_REQUEST, updateHrbpsListWorker);
  yield takeLatest(actions.REMOVE_HRBP_LIST_REQUEST, removeHrbpsListWorker);
  yield takeLatest(
    actions.UPDATE_MANAGER_HRBPS_REQUEST,
    updateManagerHrbpsWorker
  );
  yield takeLatest(actions.BULK_USER_INVITE_ASYNC, bulkInviteUsersAsyncWorker);
  yield takeLatest(
    actions.GET_ONGOING_USER_INVITE_STATUS,
    getIsOngoingUserInviteWorker
  );
  yield takeLatest(
    actions.UPDATE_USER_MANAGER_DESIGNATION_DEMOGRAPHICS,
    updateUserManagerDesignationOrDemographics
  );
}
